// The Documents list itself: one row per document, grouped by source when asked, with the
// sortable headers and the selection checkboxes. What it shows is decided by the page.

import { Fragment } from "react";
import { INCLUSION_HINTS, INCLUSION_LABELS, REVIEW_LABELS, inclusionOf } from "@ask-hr/core/documents";
import { APPLICABILITY_LABELS, AUTO_APPLICABILITY_BY, type SharePointSource } from "@ask-hr/core/sources";
import { isHidden } from "@ask-hr/core/visibility";
import { FILTERS, PILL, day, folderInSource, type Doc, type DocSortKey, type SourceGroup } from "./documents.js";
import { SortTh, type Sort } from "./sort.js";
import { label } from "./sources.js";

export function DocumentsTable({
  groups,
  shown,
  rows,
  sourceOf,
  selected,
  open,
  collapsed,
  sort,
  onSort,
  sameAudience,
  onSelectAll,
  onToggle,
  onToggleGroup,
  onOpen,
}: {
  groups: SourceGroup[] | null; // grouped by source, or null for one flat list
  shown: Doc[];
  rows: Doc[]; // what's on screen: shown, minus collapsed groups
  sourceOf: Map<string, SharePointSource>;
  selected: ReadonlySet<string>;
  open: string | null;
  collapsed: ReadonlySet<string>;
  sort: Sort<DocSortKey>;
  onSort: (k: DocSortKey) => void;
  sameAudience: (d: Doc) => boolean; // every version in its variant group applies to the same people
  onSelectAll: () => void;
  onToggle: (id: string, range: boolean) => void;
  onToggleGroup: (key: string) => void;
  onOpen: (id: string) => void;
}) {
  const allShownSelected = rows.length > 0 && rows.every((d) => selected.has(d.id));
  return (
    <div className="tablewrap tall">
      <table className="docs">
        <thead>
          <tr>
            <th scope="col" className="pick">
              <input type="checkbox" aria-label="Select all shown" checked={allShownSelected} onChange={onSelectAll} />
            </th>
            <SortTh k="document" sort={sort} onSort={onSort}>
              Document
            </SortTh>
            <SortTh k="applies" sort={sort} onSort={onSort}>
              Applies to
            </SortTh>
            <SortTh
              k="included"
              sort={sort}
              onSort={onSort}
              title={`How much of each document answers read. ${INCLUSION_LABELS.always}: ${INCLUSION_HINTS.always} ${INCLUSION_LABELS.relevant}: ${INCLUSION_HINTS.relevant}`}
            >
              Coverage
            </SortTh>
            <SortTh k="status" sort={sort} onSort={onSort}>
              Status
            </SortTh>
            <SortTh k="modified" sort={sort} onSort={onSort}>
              Modified
            </SortTh>
            <th scope="col" className="go">
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {(groups ?? [{ key: "", name: "", src: undefined, docs: shown }]).map((g) => {
            const hiddenHere = g.docs.filter((d) => isHidden(d.visibility)).length;
            const reviewHere = g.docs.filter(FILTERS.review).length;
            return (
              <Fragment key={g.key}>
                {groups ? (
                  <tr className="group">
                    <td colSpan={7}>
                      <button type="button" className="grouphead" aria-expanded={!collapsed.has(g.key)} onClick={() => onToggleGroup(g.key)}>
                        <span className="caret" aria-hidden="true">
                          {collapsed.has(g.key) ? "▸" : "▾"}
                        </span>
                        <strong>{g.name}</strong>
                      </button>
                      <span className="gmeta">
                        {g.docs.length} {g.docs.length === 1 ? "document" : "documents"}
                        {reviewHere ? ` · ${reviewHere} to review` : ""}
                        {g.src ? ` · Applies to ${APPLICABILITY_LABELS[g.src.applicability]}` : ""}
                      </span>
                      {hiddenHere ? <span className="flag lock">🔒 {hiddenHere} employees can't open</span> : null}
                    </td>
                  </tr>
                ) : null}
                {groups && collapsed.has(g.key)
                  ? null
                  : g.docs.map((d) => {
                      const src = sourceOf.get(d.sourceId);
                      const folder = folderInSource(d, src);
                      const where = groups ? folder : src ? `${label(src)}${folder}` : folder;
                      const applies = d.applicability ?? src?.applicability;
                      return (
                        <tr
                          key={d.id}
                          className={`row${selected.has(d.id) ? " picked" : ""}${open === d.id ? " active" : ""}`}
                          onClick={() => onOpen(d.id)}
                        >
                          <td className="pick" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${d.name}`}
                              title="Shift-click to select a range"
                              checked={selected.has(d.id)}
                              onChange={() => {}}
                              onClick={(e) => onToggle(d.id, e.shiftKey)}
                            />
                          </td>
                          <td>
                            <div className="docname">
                              <button type="button" className="namebtn" onClick={() => onOpen(d.id)}>
                                {d.name}
                              </button>
                              <DocFlags d={d} sameAudience={sameAudience(d)} />
                            </div>
                            {where ? <div className="muted small">{where}</div> : null}
                          </td>
                          <td>
                            {applies ? (
                              <span className={d.applicability ? undefined : "muted"}>{APPLICABILITY_LABELS[applies]}</span>
                            ) : (
                              <span className="muted">Source removed</span>
                            )}
                            {d.applicability && d.applicabilityBy?.startsWith(AUTO_APPLICABILITY_BY) ? (
                              <>
                                {" "}
                                <span className="flag auto" title={`Set by ${d.applicabilityBy}`}>
                                  auto
                                </span>
                              </>
                            ) : null}
                          </td>
                          <td className="nowrap">
                            {inclusionOf(d) === "relevant" ? INCLUSION_LABELS.relevant : <span className="muted">{INCLUSION_LABELS.always}</span>}
                          </td>
                          <td>
                            <span className={PILL[d.status]}>{REVIEW_LABELS[d.status]}</span>
                          </td>
                          <td className="muted nowrap">{day(d.modified)}</td>
                          <td className="go" aria-hidden="true">
                            ›
                          </td>
                        </tr>
                      );
                    })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Tags after a name, only for what's out of the ordinary.
function DocFlags({ d, sameAudience }: { d: Doc; sameAudience: boolean }) {
  const v = d.visibility;
  return (
    <>
      {v && isHidden(v) ? (
        d.visibilityIntendedAt ? (
          <span className="flag quiet" title="Most employees can't open it; HR marked that as intended">
            Kept private
          </span>
        ) : v.state === "link" ? (
          <span className="flag link" title="Only an organization link; the address answers cite is closed to most employees">
            Org link only
          </span>
        ) : (
          <span className="flag lock" title={`${v.reach} of ${v.of} people in Ask HR - Users can open it`}>
            🔒 Can't open
          </span>
        )
      ) : null}
      {v?.state === "unknown" ? (
        <span className="flag quiet" title="Ask HR couldn't list everyone who has access">
          Can't tell
        </span>
      ) : null}
      {d.variantGroup ? (
        <span
          className="flag var"
          title={`Location variants: ${d.variantGroup}${sameAudience ? ". Every version applies to the same people." : ""}`}
        >
          Variant{sameAudience ? " ⚠" : ""}
        </span>
      ) : null}
      {d.current?.readFrom === "images" ? (
        <span className="flag quiet" title="A scanned PDF: its text was read from the page images">
          Scanned
        </span>
      ) : null}
    </>
  );
}
